import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, FileText, Download, Mail, Wrench, Instagram, ArrowLeft, Plus, Trash2, XCircle, ExternalLink,
  Zap, RefreshCw, CheckCircle2, Circle, Send, Clock, Play, AlertTriangle, Calendar, Share2, Copy, QrCode, Car, Ticket,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import VehicleDbTab from '@/components/admin/VehicleDbTab';
import TrialPassesTab from '@/components/admin/TrialPassesTab';
import GoogleCalendarPanel from '@/components/admin/GoogleCalendarPanel';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { TID } from '@/testIds';

const TABS = [
  { id: 'quotes',       label: 'Quotes',           icon: FileText },
  { id: 'appointments', label: 'Appointments',     icon: Calendar },
  { id: 'partners',     label: 'Partner apps',     icon: Users },
  { id: 'rate_leads',   label: 'Rate sheet leads', icon: Download },
  { id: 'contacts',     label: 'Contacts',         icon: Mail },
  { id: 'installers',   label: 'Installers',       icon: Wrench },
  { id: 'portfolio',    label: 'Portfolio',        icon: Instagram },
  { id: 'vehicles',     label: 'Vehicle DB',       icon: Car },
  { id: 'trials',       label: 'Access & Trials',  icon: Ticket },
  { id: 'integrations', label: 'Integrations',     icon: Zap },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function AdminPage({ embed = false }) {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'quotes');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Return from Google OAuth: /admin?tab=integrations&gcal=connected|error
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const g = qs.get('gcal');
    if (!g) return;
    if (g === 'connected') toast.success('Google Calendar connected — new bookings will sync automatically.');
    else toast.error(`Google Calendar connection failed${qs.get('reason') ? `: ${qs.get('reason')}` : ''}`);
    window.history.replaceState({}, '', '/admin');
  }, []);

  const loadSummary = useCallback(() => {
    api.get('/admin/summary').then(({ data }) => setSummary(data.counts))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load summary'));
  }, []);

  const loadTab = useCallback((k) => {
    setLoading(true); setItems([]);
    if (k === 'integrations' || k === 'appointments' || k === 'vehicles' || k === 'trials') { setLoading(false); return; }
    const url = k === 'portfolio' ? '/admin/portfolio' : `/admin/leads?kind=${k}`;
    api.get(url)
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {!embed && <Header />}
      <main className={embed ? 'pt-4 pb-8' : 'pt-28 pb-20'}>
        <div className={embed ? 'px-4' : 'max-w-[1400px] mx-auto px-6 lg:px-10'}>
          {!embed && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2">
              <ArrowLeft className="w-3 h-3" /> BACK TO HOME
            </Link>
            <div className="flex items-center gap-4">
              <div className="mi-eyebrow text-white/60 text-[10px]">
                ADMIN · <span className="text-white">{user?.email}</span>
              </div>
              <button onClick={logout} className="mi-btn-ghost text-xs py-2 px-3">
                <XCircle className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </div>
          )}

          {!embed && (
          <div className="mb-8">
            <div className="mi-eyebrow mb-2 text-[#E63946]">// ADMIN CONSOLE</div>
            <h1 className="mi-headline text-5xl md:text-6xl">
              Every lead. <span className="text-[#E63946]">One place.</span>
            </h1>
          </div>
          )}

          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={TID.adminTab(t.id)}
                className={`p-4 border text-left transition-colors ${
                  tab === t.id ? 'border-[#E63946] bg-[#E63946]/10' : 'mi-line-strong bg-[#0A0A0A] hover:bg-white/[0.03]'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <t.icon className="w-3.5 h-3.5 text-[#E63946]" />
                  <span className="mi-eyebrow text-[10px] text-white/60">{t.label}</span>
                </div>
                <div className="mi-headline text-3xl">{summary?.[t.id] ?? '—'}</div>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="mi-surface p-6">
            {tab === 'portfolio'
              ? <PortfolioTab items={items} loading={loading} reload={() => { loadTab('portfolio'); loadSummary(); }} />
              : tab === 'integrations'
              ? <IntegrationsTab />
              : tab === 'appointments'
              ? <AppointmentsTab />
              : tab === 'vehicles'
              ? <VehicleDbTab />
              : tab === 'trials'
              ? <TrialPassesTab />
              : <LeadTable kind={tab} items={items} loading={loading} />}
          </div>
        </div>
      </main>
      {!embed && <Footer />}
    </div>
  );
}

/* ---------- Lead table (quotes / partners / rate_leads / contacts / installers) ---------- */
function LeadTable({ kind, items, loading }) {
  if (loading) return <div className="text-white/50 text-sm">Loading…</div>;
  if (!items.length) return <div className="text-white/50 text-sm">No entries yet.</div>;

  const columns = COLS[kind] || COLS.default;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] mi-eyebrow text-white/50 bg-black/40">
            {columns.map((c) => <th key={c.k} className="text-left px-4 py-3">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-t mi-line hover:bg-white/[0.02] align-top">
              {columns.map((c) => (
                <td key={c.k} className="px-4 py-3 text-white/85">{c.render ? c.render(row) : row[c.k] || <span className="text-white/25">—</span>}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const COLS = {
  quotes: [
    { k: 'created_at', label: 'When',     render: (r) => <span className="text-white/60">{fmtDate(r.created_at)}</span> },
    { k: 'project_type', label: 'Type',   render: (r) => <span className="mi-tag text-[10px]">{r.project_type}</span> },
    { k: 'name',       label: 'Name',     render: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() },
    { k: 'email',      label: 'Email',    render: (r) => <a className="text-[#E63946]" href={`mailto:${r.email}`}>{r.email}</a> },
    { k: 'company',    label: 'Company' },
    { k: 'details',    label: 'Details',  render: (r) => <span className="text-white/70 line-clamp-2 block max-w-md">{r.details}</span> },
  ],
  partners: [
    { k: 'created_at', label: 'When',     render: (r) => <span className="text-white/60">{fmtDate(r.created_at)}</span> },
    { k: 'company_name', label: 'Company' },
    { k: 'contact_name', label: 'Contact' },
    { k: 'email',      label: 'Email',    render: (r) => <a className="text-[#E63946]" href={`mailto:${r.email}`}>{r.email}</a> },
    { k: 'business_type', label: 'Type',  render: (r) => <span className="mi-tag text-[10px]">{r.business_type}</span> },
    { k: 'monthly_wrap_volume', label: 'Volume', render: (r) => <span className="font-mono text-xs">{r.monthly_wrap_volume}/mo</span> },
    { k: 'location',   label: 'Location', render: (r) => [r.city, r.state].filter(Boolean).join(', ') || '—' },
  ],
  rate_leads: [
    { k: 'created_at', label: 'When',     render: (r) => <span className="text-white/60">{fmtDate(r.created_at)}</span> },
    { k: 'email',      label: 'Email',    render: (r) => <a className="text-[#E63946]" href={`mailto:${r.email}`}>{r.email}</a> },
    { k: 'company',    label: 'Company' },
    { k: 'role',       label: 'Role' },
    { k: 'source',     label: 'Source',   render: (r) => <span className="mi-tag text-[10px]">{r.source || 'gate'}</span> },
  ],
  contacts: [
    { k: 'created_at', label: 'When',    render: (r) => <span className="text-white/60">{fmtDate(r.created_at)}</span> },
    { k: 'name',       label: 'Name' },
    { k: 'email',      label: 'Email',   render: (r) => <a className="text-[#E63946]" href={`mailto:${r.email}`}>{r.email}</a> },
    { k: 'phone',      label: 'Phone' },
    { k: 'message',    label: 'Message', render: (r) => <span className="text-white/70 line-clamp-2 block max-w-md">{r.message}</span> },
  ],
  installers: [
    { k: 'created_at', label: 'When',     render: (r) => <span className="text-white/60">{fmtDate(r.created_at)}</span> },
    { k: 'name',       label: 'Name' },
    { k: 'email',      label: 'Email',    render: (r) => <a className="text-[#E63946]" href={`mailto:${r.email}`}>{r.email}</a> },
    { k: 'location',   label: 'Location', render: (r) => [r.city, r.state].filter(Boolean).join(', ') || '—' },
    { k: 'years_experience', label: 'Yrs', render: (r) => r.years_experience ? `${r.years_experience} yrs` : '—' },
  ],
  default: [
    { k: 'id',         label: 'ID' },
    { k: 'created_at', label: 'When',    render: (r) => fmtDate(r.created_at) },
  ],
};

/* ---------- Portfolio (Instagram admin) ---------- */
function PortfolioTab({ items, loading, reload }) {
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/admin/portfolio', { url, caption: caption || undefined });
      toast.success('Post added to portfolio.');
      setUrl(''); setCaption('');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add post');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this post from the portfolio?')) return;
    try {
      await api.delete(`/admin/portfolio/${id}`);
      toast.success('Removed.');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="mi-eyebrow mb-3 text-[#E63946]">// PORTFOLIO · INSTAGRAM FEED</div>
      <form onSubmit={submit} className="grid md:grid-cols-[2fr_1.5fr_auto] gap-3 mb-6">
        <input
          required type="url"
          placeholder="https://www.instagram.com/reel/…"
          value={url} onChange={(e) => setUrl(e.target.value)}
          data-testid={TID.adminPortfolioUrl}
          className="bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm placeholder-white/40 hover:border-white/25"
        />
        <input
          type="text"
          placeholder="Caption / label (optional)"
          value={caption} onChange={(e) => setCaption(e.target.value)}
          data-testid={TID.adminPortfolioCaption}
          className="bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm placeholder-white/40 hover:border-white/25"
        />
        <button
          type="submit" disabled={busy || !url}
          data-testid={TID.adminPortfolioSubmit}
          className="mi-btn-red disabled:opacity-60"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>
      <p className="text-white/50 text-xs mb-6">
        Paste any Instagram post, reel or IGTV URL — the shortcode is auto-extracted and the tile renders on your public portfolio.
      </p>

      {loading ? (
        <div className="text-white/50 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-white/50 text-sm">
          No posts yet — the public portfolio will show the default gallery until you add some.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((p) => (
            <div key={p.id} className="border mi-line-strong bg-[#0A0A0A] p-3 flex gap-3">
              <div className="w-24 h-24 border mi-line flex-shrink-0 overflow-hidden">
                <iframe
                  title={p.shortcode}
                  src={p.embed_url}
                  loading="lazy"
                  scrolling="no"
                  className="w-[300px] h-[300px] scale-[0.32] origin-top-left pointer-events-none"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[#E63946] text-xs font-bold truncate">{p.shortcode}</div>
                {p.caption && <div className="text-white/85 text-sm mt-1 line-clamp-2">{p.caption}</div>}
                <div className="mi-eyebrow text-[9px] text-white/40 mt-1">{fmtDate(p.created_at)}</div>
                <div className="mt-2 flex gap-2">
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                     className="mi-eyebrow text-[10px] text-white/60 hover:text-white inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> OPEN
                  </a>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={TID.adminPortfolioDelete(p.id)}
                    className="mi-eyebrow text-[10px] text-white/50 hover:text-[#E63946] inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> DELETE
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Integrations (Instagram + Resend) ---------- */
function IntegrationsTab() {
  const [juicer, setJuicer] = useState(null);
  const [resend, setResend] = useState(null);
  const [busy, setBusy] = useState(null); // 'sync'|'purge'

  const load = useCallback(async () => {
    try {
      const [{ data: jData }, { data: rData }] = await Promise.all([
        api.get('/juicer/status'),
        api.get('/admin/resend-domain'),
      ]);
      setJuicer(jData); setResend(rData);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load integration status');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy('sync');
    try { const { data } = await api.post('/juicer/sync'); toast.success(`Synced ${data.synced} posts.`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
    finally { setBusy(null); }
  };
  const purge = async () => {
    if (!window.confirm('Purge all cached Juicer posts? They will re-sync on the next tick.')) return;
    setBusy('purge');
    try { const { data } = await api.post('/juicer/purge'); toast.success(`Purged ${data.deleted} posts.`); await load(); }
    catch (e) { toast.error('Purge failed'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      {/* Scheduled jobs */}
      <ScheduledJobsPanel />

      {/* Google Calendar */}
      <GoogleCalendarPanel />

      {/* Juicer.io */}
      <div>
        <div className="mi-eyebrow mb-3 text-[#E63946]">// JUICER.IO · SOCIAL FEED</div>
        <div className="border mi-line-strong bg-[#0A0A0A] p-6">
          {!juicer ? (
            <div className="text-white/50 text-sm">Loading…</div>
          ) : (
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-[#E63946]" />
                  <span className="mi-headline text-2xl">Feed: {juicer.feed_slug}</span>
                </div>
                <div className="mi-eyebrow text-white/60 text-[10px] mb-3">
                  Sources: {(juicer.sources && juicer.sources.length) ? juicer.sources.join(' · ') : '—'}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-white/50 text-xs mb-1">POSTS CACHED</div>
                    <div className="mi-headline text-2xl" data-testid="juicer-post-count">{juicer.post_count}</div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs mb-1">LAST SYNC</div>
                    <div className="font-mono text-xs text-white/85">
                      {juicer.last_synced_at ? new Date(juicer.last_synced_at).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-white/50 text-xs">
                  Manage your feed at{' '}
                  <a href="https://www.juicer.io/dashboard" target="_blank" rel="noopener noreferrer"
                     className="text-[#E63946] hover:underline inline-flex items-center gap-1">
                    juicer.io/dashboard <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="flex flex-col gap-2 min-w-[200px]">
                <button onClick={sync} disabled={busy === 'sync'} className="mi-btn-red text-xs" data-testid="juicer-sync-btn">
                  <RefreshCw className={`w-3.5 h-3.5 ${busy === 'sync' ? 'animate-spin' : ''}`} /> Sync now
                </button>
                <button onClick={purge} disabled={busy === 'purge'} className="mi-btn-ghost text-xs" data-testid="juicer-purge-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Purge cache
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resend */}
      <div>
        <div className="mi-eyebrow mb-3 text-[#E63946]">// RESEND · DOMAIN &amp; SENDER</div>
        <div className="border mi-line-strong bg-[#0A0A0A] p-6">
          {!resend ? <div className="text-white/50 text-sm">Loading…</div> : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="mi-headline text-2xl">{resend.name || '—'}</div>
                <span className={`mi-tag text-[10px] ${resend.status === 'verified' ? 'mi-tag-red' : ''}`}>
                  STATUS · {(resend.status || 'unknown').toUpperCase()}
                </span>
                <span className="mi-tag text-[10px]"><Send className="w-3 h-3" /> {resend.sender || 'sender not set'}</span>
                <button onClick={load} className="mi-btn-ghost text-xs ml-auto">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
              </div>
              <div className="text-white/60 text-xs mb-4">
                {resend.status === 'verified'
                  ? `Domain verified — all Motor iNKS emails are sending from ${resend.sender || 'sales@motorinks.com'}.`
                  : 'Add these DNS records at your registrar. Verification usually completes within 30 minutes of DNS propagation.'}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] mi-eyebrow text-white/50 bg-black/40">
                      <th className="text-left px-3 py-2">TYPE</th>
                      <th className="text-left px-3 py-2">HOST</th>
                      <th className="text-left px-3 py-2">VALUE</th>
                      <th className="text-left px-3 py-2">PRI</th>
                      <th className="text-left px-3 py-2">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(resend.records || []).map((r, i) => (
                      <tr key={i} className="border-t mi-line">
                        <td className="px-3 py-2 font-mono text-[#E63946]">{r.type}</td>
                        <td className="px-3 py-2 font-mono">{r.name}</td>
                        <td className="px-3 py-2 font-mono text-white/80 break-all max-w-md">{r.value}</td>
                        <td className="px-3 py-2 text-white/60">{r.priority ?? '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`mi-tag text-[9px] ${r.status === 'verified' ? 'mi-tag-red' : ''}`}>
                            {(r.status || 'pending').toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ---------- Scheduled jobs (IG sync + aftercare renewals) ---------- */
const JOB_META = {
  juicer_sync: {
    label: 'Juicer feed auto-sync',
    caption: 'Pulls latest posts from Juicer.io',
    icon: Instagram,
  },
  aftercare_renewals: {
    label: 'Aftercare renewal reminders',
    caption: 'Daily at 00:00 UTC · emails partners 7 days before next billing',
    icon: Mail,
  },
  appointment_reminders: {
    label: 'Appointment reminders',
    caption: 'Daily at 08:00 UTC · emails sales@ for appointments ~24h out',
    icon: Clock,
  },
  gcal_pull: {
    label: 'Google Calendar pull',
    caption: 'Every 15 min · blocks slots for events on your Google Calendar; applies moves / deletions of website bookings',
    icon: Calendar,
  },
  trial_emails: {
    label: 'Trial pass emails',
    caption: 'Daily at 14:00 UTC · day-27 "ending soon" + day-31 "convert to paid" to trial pass holders',
    icon: Ticket,
  },
};

const lastSummary = (id, st) => {
  if (id === 'juicer_sync') return st.last_reason ? `${st.last_status} (${st.last_reason})` : st.last_synced !== undefined ? `synced ${st.last_synced}` : '—';
  if (id === 'gcal_pull') return st.last_reason ? `skipped (${st.last_reason})` : st.scanned !== undefined ? `${st.blocks} blocks · ${st.moved} moved · ${st.canceled} canceled` : '—';
  if (id === 'trial_emails') return st.scanned !== undefined ? `${st.ending_sent} ending · ${st.ended_sent} ended · ${st.skipped} skipped` : '—';
  return st.sent !== undefined ? `sent ${st.sent} / ${st.in_window ?? st.scanned ?? '—'} in window` : '—';
};

const fmtNext = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffMs = d - new Date();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  const rel = mins < 60 ? `in ${mins}m` :
              mins < 60 * 24 ? `in ${Math.round(mins / 60)}h` :
              `in ${Math.round(mins / 60 / 24)}d`;
  return `${d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })} · ${rel}`;
};

function ScheduledJobsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // job_id currently running
  const [intervalBusy, setIntervalBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/jobs');
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load jobs');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (jobId) => {
    setBusy(jobId);
    try {
      const { data } = await api.post(`/admin/jobs/${jobId}/run`);
      const r = data.result || {};
      if (jobId === 'juicer_sync') {
        toast.success(r.status === 'skipped'
          ? `Feed sync skipped — ${r.reason || 'unknown reason'}.`
          : r.status === 'ok' ? `Feed sync ok — ${r.synced} posts.`
          : `Feed sync error: ${r.error || 'unknown'}`);
      } else if (jobId === 'gcal_pull') {
        toast.success(r.status === 'skipped' ? `Google pull skipped — ${r.skipped || 'not connected'}.`
          : r.status === 'ok' ? `Google pull ok — ${r.blocks} blocks · ${r.moved} moved · ${r.canceled} canceled.`
          : `Google pull error: ${r.error || 'unknown'}`);
      } else if (jobId === 'trial_emails') {
        toast.success(r.status === 'ok' ? `Trial emails · ${r.ending_sent} ending · ${r.ended_sent} ended · ${r.skipped} skipped` : `Trial emails error: ${r.error || 'unknown'}`);
      } else if (jobId === 'appointment_reminders') {
        toast.success(r.status === 'ok' ? `Reminders · sent ${r.sent} / ${r.scanned} in window / skipped ${r.skipped}` : `Reminders error: ${r.error || 'unknown'}`);
      } else {
        toast.success(`Renewal scan · sent ${r.sent} / in window ${r.in_window} / skipped ${r.skipped}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Manual run failed');
    } finally {
      setBusy(null);
    }
  };

  const setFeedInterval = async (minutes) => {
    setIntervalBusy(true);
    try {
      await api.put('/admin/jobs/juicer_sync/interval', { minutes });
      toast.success(`Feed sync interval set to ${minutes} min.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update interval');
    } finally {
      setIntervalBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="mi-eyebrow text-[#E63946]">// SCHEDULED JOBS · BACKGROUND</div>
        <button onClick={load} data-testid={TID.adminJobsRefresh} className="mi-btn-ghost text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {!data ? (
        <div className="border mi-line-strong bg-[#0A0A0A] p-6 text-white/50 text-sm">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {(data.jobs || []).map((j) => {
            const meta = JOB_META[j.id] || { label: j.id, caption: '', icon: Clock };
            const st = data.state?.[j.id] || {};
            const status = st.last_status;
            const dotColor = status === 'ok' ? 'text-[#E63946]'
                          : status === 'error' ? 'text-yellow-400'
                          : 'text-white/40';
            return (
              <div key={j.id} className="border mi-line-strong bg-[#0A0A0A] p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <meta.icon className="w-4 h-4 text-[#E63946]" />
                    <span className="mi-headline text-lg leading-tight">{meta.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {st.failure_alert_sent && (
                      <span className="mi-tag text-[9px] inline-flex items-center gap-1 border-yellow-400/60 text-yellow-400">
                        <AlertTriangle className="w-3 h-3" /> ALERTED
                      </span>
                    )}
                    <span className={`mi-tag text-[9px] inline-flex items-center gap-1 ${status === 'ok' ? 'mi-tag-red' : ''}`}>
                      {status === 'error' && <AlertTriangle className="w-3 h-3" />}
                      {status ? status.toUpperCase() : 'IDLE'}
                    </span>
                  </div>
                </div>
                <div className="text-white/60 text-xs mb-4">
                  {j.id === 'juicer_sync'
                    ? `Runs every ${data.config?.feed_sync_interval_minutes || 30} min · pulls latest posts from Juicer.io`
                    : meta.caption}
                </div>

                {j.id === 'juicer_sync' && (
                  <div className="mb-4 flex items-center gap-3">
                    <span className="mi-eyebrow text-white/40 text-[9px]">FREQUENCY</span>
                    <div className="inline-flex border mi-line rounded-sm overflow-hidden">
                      {(data.config?.feed_sync_allowed_minutes || [15, 30, 60]).map((m) => {
                        const active = (data.config?.feed_sync_interval_minutes || 30) === m;
                        return (
                          <button
                            key={m}
                            onClick={() => setFeedInterval(m)}
                            disabled={intervalBusy || active}
                            data-testid={`admin-feed-interval-${m}`}
                            className={`px-3 py-1 text-xs font-mono transition-colors ${
                              active
                                ? 'bg-[#E63946] text-white'
                                : 'bg-transparent text-white/70 hover:bg-white/[0.05]'
                            } disabled:opacity-60`}
                          >
                            {m}m
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="mi-eyebrow text-white/40 text-[9px] mb-1">NEXT RUN</div>
                    <div className="font-mono text-white/85">{fmtNext(j.next_run)}</div>
                  </div>
                  <div>
                    <div className="mi-eyebrow text-white/40 text-[9px] mb-1">LAST</div>
                    <div className={`font-mono ${dotColor}`}>
                      {lastSummary(j.id, st)}
                    </div>
                    <div className="text-white/40 text-[10px] mt-1">
                      {st.updated_at ? new Date(st.updated_at).toLocaleString() : ''}
                    </div>
                  </div>
                </div>

                {st.last_error && (
                  <div className="mt-3 text-xs text-yellow-400 font-mono break-all line-clamp-2">{st.last_error}</div>
                )}

                <button
                  onClick={() => run(j.id)}
                  disabled={busy === j.id}
                  data-testid={TID.adminJobRun(j.id)}
                  className="mi-btn-ghost text-xs mt-4 w-full justify-center"
                >
                  <Play className={`w-3.5 h-3.5 ${busy === j.id ? 'animate-pulse' : ''}`} />
                  {busy === j.id ? 'Running…' : 'Run now'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {data && (
        <div className="mt-4 flex items-center gap-2 text-white/50 text-xs">
          <Mail className="w-3.5 h-3.5 text-[#E63946]" />
          <span className="font-mono">{data.renewal_emails_this_month || 0}</span>
          <span>renewal emails sent this month</span>
        </div>
      )}
    </div>
  );
}

/* ---------- Appointments tab (bookings + schedule settings) ---------- */
const STATUS_META = {
  pending:   { color: 'text-yellow-400 border-yellow-400/50', label: 'PENDING' },
  confirmed: { color: 'text-[#E63946] border-[#E63946]/60',    label: 'CONFIRMED' },
  completed: { color: 'text-green-400 border-green-400/50',    label: 'COMPLETED' },
  canceled:  { color: 'text-white/40 border-white/20',          label: 'CANCELED' },
};

const fmtWhen = (iso, tz) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone: tz || 'America/Chicago',
    });
  } catch { return iso; }
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: a }, { data: s }, blk] = await Promise.all([
        api.get('/admin/schedule/appointments'),
        api.get('/admin/schedule/settings'),
        api.get('/admin/gcal/blocks').catch(() => ({ data: { items: [] } })),
      ]);
      setItems(a.items || []);
      setSettings(s);
      setBlocks(blk.data.items || []);
    } catch (e) {
      toast.error('Failed to load appointments');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/schedule/appointments/${id}`, { status });
      toast.success(`Marked ${status}.`);
      await load();
    } catch { toast.error('Could not update'); }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put('/admin/schedule/settings', patch);
      setSettings(data);
      toast.success('Schedule settings saved.');
    } catch { toast.error('Save failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mi-eyebrow text-[#E63946]">// APPOINTMENTS · ON-SITE BOOKINGS</div>
          <div className="text-white/60 text-xs mt-1">Every booking sends a .ics invite to sales@motorinks.com and the customer.</div>
        </div>
        <button onClick={() => setShowSettings((v) => !v)} data-testid="admin-appt-settings-toggle" className="mi-btn-ghost text-xs">
          <Clock className="w-3.5 h-3.5" /> {showSettings ? 'Hide settings' : 'Schedule settings'}
        </button>
      </div>

      <ShareBookingPanel />

      {blocks.length > 0 && (
        <div className="border border-[#E63946]/30 bg-[#E63946]/[0.03] p-4" data-testid="gcal-blocks-panel">
          <div className="mi-eyebrow text-[#E63946] text-[10px] mb-2 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" /> // BLOCKED BY GOOGLE CALENDAR · {blocks.length} UPCOMING
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {blocks.slice(0, 12).map((b) => (
              <a key={b.event_id} href={b.html_link || '#'} target="_blank" rel="noopener noreferrer"
                 data-testid={`gcal-block-${b.event_id}`}
                 className="border mi-line bg-black/40 p-2.5 text-xs hover:border-[#E63946]/60">
                <div className="text-white truncate">{b.summary}</div>
                <div className="text-white/50 font-mono text-[10px] mt-0.5">
                  {b.all_day ? `${b.start_time.slice(0, 10)} · all day` : fmtWhen(b.start_time, settings?.timezone)}
                </div>
              </a>
            ))}
          </div>
          <div className="text-white/40 text-[10px] mt-2">These times are hidden from the public booking widget. Mark an event "Free" in Google to stop it blocking.</div>
        </div>
      )}

      {showSettings && settings && (
        <ScheduleSettingsPanel settings={settings} onSave={saveSettings} />
      )}

      {busy ? (
        <div className="text-white/50 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-6 border mi-line-strong bg-black/40 text-white/60 text-sm text-center">
          No bookings yet. When customers book from motorinks.com/schedule they'll appear here.
        </div>
      ) : (
        <div className="border mi-line-strong overflow-x-auto">
          <table className="w-full text-xs" data-testid="admin-appt-table">
            <thead>
              <tr className="bg-black/60">
                {['WHEN', 'CUSTOMER', 'SERVICE', 'VEHICLE', 'STATUS', ''].map((h) => (
                  <th key={h} className="p-3 text-left mi-eyebrow text-white/50 text-[9px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const meta = STATUS_META[b.status] || STATUS_META.pending;
                const vehicle = [b.vehicle_year, b.vehicle_make, b.vehicle_model].filter(Boolean).join(' ');
                return (
                  <tr key={b.id} className="border-t mi-line hover:bg-white/[0.02]">
                    <td className="p-3 font-mono text-white/85 min-w-[180px]">
                      {fmtWhen(b.start_time, b.timezone)}
                      {b.gcal_event_id && (
                        <a href={b.gcal_event_link || 'https://calendar.google.com'} target="_blank" rel="noopener noreferrer"
                           data-testid={`gcal-badge-${b.id}`} title="On Google Calendar"
                           className="ml-2 inline-flex items-center gap-1 text-[9px] mi-eyebrow text-[#E63946]/80 hover:text-[#E63946]">
                          <Calendar className="w-3 h-3" /> GCAL
                        </a>
                      )}
                      {!b.gcal_event_id && b.gcal_error && b.status !== 'canceled' && (
                        <span title={b.gcal_error} data-testid={`gcal-error-${b.id}`} className="ml-2 inline-flex items-center gap-1 text-[9px] mi-eyebrow text-yellow-400">
                          <AlertTriangle className="w-3 h-3" /> SYNC
                        </span>
                      )}
                      {b.gcal_moved_from_google_at && (
                        <div className="text-[9px] mi-eyebrow text-white/40 mt-1" title={`Was ${fmtWhen(b.previous_start_time, b.timezone)}`}>moved in Google</div>
                      )}
                      {b.gcal_canceled_from_google_at && (
                        <div className="text-[9px] mi-eyebrow text-white/40 mt-1">deleted in Google</div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="text-white">{b.name}</div>
                      <div className="text-white/50 text-[10px]">
                        <a href={`mailto:${b.email}`} className="hover:text-[#E63946]">{b.email}</a> · {b.phone}
                      </div>
                    </td>
                    <td className="p-3 text-white/80">{b.service_type}</td>
                    <td className="p-3 text-white/60">
                      {vehicle || '—'}
                      {b.source && b.source !== 'direct' && (
                        <div className="mi-eyebrow text-[9px] text-[#E63946]/80 mt-1 truncate">via {b.source}</div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`mi-tag text-[9px] ${meta.color}`}>{meta.label}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1.5">
                        {b.status !== 'confirmed' && <button onClick={() => updateStatus(b.id, 'confirmed')} className="mi-btn-ghost text-[10px] px-2 py-1">Confirm</button>}
                        {b.status !== 'completed' && <button onClick={() => updateStatus(b.id, 'completed')} className="mi-btn-ghost text-[10px] px-2 py-1">Done</button>}
                        {b.status !== 'canceled' && <button onClick={() => updateStatus(b.id, 'canceled')} className="mi-btn-ghost text-[10px] px-2 py-1 text-white/50">Cancel</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScheduleSettingsPanel({ settings, onSave }) {
  const [local, setLocal] = useState(settings);
  useEffect(() => setLocal(settings), [settings]);
  const toggleDay = (d) => {
    const next = local.weekdays.includes(d)
      ? local.weekdays.filter((w) => w !== d)
      : [...local.weekdays, d].sort();
    setLocal({ ...local, weekdays: next });
  };
  const blockedText = (local.blocked_dates || []).join(', ');

  return (
    <div className="border-2 border-[#E63946]/40 bg-[#E63946]/[0.04] p-5 space-y-4" data-testid="admin-appt-settings">
      <div className="mi-eyebrow text-[#E63946] text-[10px]">// EDIT SCHEDULE</div>
      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">Open hour (24h)</div>
          <input type="number" min={0} max={23} value={local.open_hour}
            onChange={(e) => setLocal({ ...local, open_hour: parseInt(e.target.value, 10) })}
            className="w-full bg-black border mi-line-strong text-white px-3 py-2" />
        </label>
        <label className="block">
          <div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">Close hour (24h)</div>
          <input type="number" min={0} max={23} value={local.close_hour}
            onChange={(e) => setLocal({ ...local, close_hour: parseInt(e.target.value, 10) })}
            className="w-full bg-black border mi-line-strong text-white px-3 py-2" />
        </label>
        <label className="block">
          <div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">Slot duration (min)</div>
          <input type="number" min={15} step={15} value={local.duration_minutes}
            onChange={(e) => setLocal({ ...local, duration_minutes: parseInt(e.target.value, 10) })}
            className="w-full bg-black border mi-line-strong text-white px-3 py-2" />
        </label>
        <label className="block">
          <div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">Buffer between (min)</div>
          <input type="number" min={0} step={5} value={local.buffer_minutes}
            onChange={(e) => setLocal({ ...local, buffer_minutes: parseInt(e.target.value, 10) })}
            className="w-full bg-black border mi-line-strong text-white px-3 py-2" />
        </label>
      </div>
      <div>
        <div className="mi-eyebrow text-white/60 text-[10px] mb-2">Open days</div>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAY_LABELS.map((lbl, i) => {
            const on = local.weekdays.includes(i);
            return (
              <button key={i} onClick={() => toggleDay(i)}
                className={`px-3 py-1.5 text-xs mi-headline border-2 transition-colors ${on ? 'border-[#E63946] bg-[#E63946] text-white' : 'mi-line-strong text-white/50'}`}>
                {lbl}
              </button>
            );
          })}
        </div>
      </div>
      <label className="block">
        <div className="mi-eyebrow text-white/60 text-[10px] mb-1.5">Blocked dates (comma-separated YYYY-MM-DD)</div>
        <input
          value={blockedText}
          onChange={(e) => setLocal({ ...local, blocked_dates: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder="2026-12-24, 2026-12-25"
          className="w-full bg-black border mi-line-strong text-white px-3 py-2 font-mono text-xs" />
      </label>
      <button onClick={() => onSave(local)} data-testid="admin-appt-save-settings" className="mi-btn-red text-xs">
        Save settings
      </button>
    </div>
  );
}


/* ---------- Share Booking (public widget URLs + QR) ---------- */
const BOOKING_BASE = `${window.location.origin}/book`;
const EMBED_BASE   = `${window.location.origin}/embed/schedule`;

const SHARE_CHANNELS = [
  { key: 'gbp',       label: 'Google Business Profile', hint: 'Paste as the "Book" or "Appointments" link in your GBP dashboard.', src: 'gbp',       med: 'business_profile', color: '#4285F4' },
  { key: 'facebook',  label: 'Facebook Page',           hint: 'Set as the "Book Now" call-to-action button URL on your Page.',   src: 'facebook',  med: 'social',            color: '#1877F2' },
  { key: 'instagram', label: 'Instagram Bio',           hint: 'Paste as the single link in your bio (or add to Linktree).',      src: 'instagram', med: 'social',            color: '#E4405F' },
  { key: 'tiktok',    label: 'TikTok Bio',              hint: 'Add to your TikTok profile link. Same short URL as everywhere.',  src: 'tiktok',    med: 'social',            color: '#00F2EA' },
  { key: 'flyer',     label: 'Flyer / QR / Print',      hint: 'Print the QR on shop signage, business cards, van decals, etc.', src: 'flyer',     med: 'offline',           color: '#E63946' },
];

const buildShareUrl = (base, ch) =>
  `${base}?utm_source=${ch.src}&utm_medium=${ch.med}&utm_campaign=booking`;

// Free QR endpoint — server-side rendering, no library, downloadable PNG.
const qrUrl = (url, size = 320) =>
  `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url)}&size=${size}x${size}&margin=8&format=svg`;

function ShareBookingPanel() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState('');

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
      toast.success('Link copied');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="border-2 border-[#E63946]/40 bg-black/40" data-testid="admin-share-booking">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-share-booking-toggle"
        className="w-full flex items-center justify-between p-4 hover:bg-white/[0.03]"
      >
        <div className="flex items-center gap-3">
          <Share2 className="w-4 h-4 text-[#E63946]" />
          <div className="text-left">
            <div className="mi-headline text-lg leading-tight">Share booking widget</div>
            <div className="text-white/50 text-[10px] mi-eyebrow">GBP · FACEBOOK · INSTAGRAM · TIKTOK · QR</div>
          </div>
        </div>
        <div className="mi-eyebrow text-[10px] text-[#E63946]">{open ? 'HIDE' : 'REVEAL'} →</div>
      </button>

      {open && (
        <div className="border-t mi-line p-5 space-y-5">
          <div className="text-white/60 text-xs leading-relaxed">
            Every link below is <span className="text-white font-mono">/book</span> pre-tagged with a UTM so you'll see the source of every appointment on the row.
          </div>

          <div className="grid gap-3">
            {SHARE_CHANNELS.map((ch) => {
              const url = buildShareUrl(BOOKING_BASE, ch);
              const embedUrl = buildShareUrl(EMBED_BASE, ch);
              const isFlyer = ch.key === 'flyer';
              return (
                <div key={ch.key} className="border mi-line-strong bg-[#0A0A0A] p-4" data-testid={`share-channel-${ch.key}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2" style={{ background: ch.color }} />
                        <span className="mi-headline text-base leading-tight">{ch.label}</span>
                      </div>
                      <div className="text-white/50 text-[11px] mb-3">{ch.hint}</div>
                      <div className="flex flex-col gap-2">
                        <ShareRow label="Public URL"    url={url}      copied={copied === `${ch.key}-url`}   onCopy={() => copy(url, `${ch.key}-url`)} testId={`share-url-${ch.key}`} />
                        {!isFlyer && (
                          <ShareRow label="Embed (iframe)" url={embedUrl} copied={copied === `${ch.key}-embed`} onCopy={() => copy(embedUrl, `${ch.key}-embed`)} testId={`share-embed-${ch.key}`} />
                        )}
                      </div>
                    </div>
                    {isFlyer && (
                      <div className="flex-shrink-0 text-center">
                        <img
                          src={qrUrl(url, 160)}
                          alt="Booking QR code"
                          width={110} height={110}
                          data-testid="share-qr-image"
                          className="border-2 border-white bg-white p-1"
                        />
                        <a
                          href={qrUrl(url, 800)}
                          download="motorinks-booking-qr.svg"
                          data-testid="share-qr-download"
                          className="mt-1.5 inline-flex items-center gap-1 mi-eyebrow text-[9px] text-[#E63946] hover:underline"
                        >
                          <QrCode className="w-3 h-3" /> DOWNLOAD SVG
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-white/40 text-[10px] mi-eyebrow border-t mi-line pt-3">
            TIP · Once a booking rolls in, expand the row header to see which channel drove it.
          </div>
        </div>
      )}
    </div>
  );
}

function ShareRow({ label, url, copied, onCopy, testId }) {
  return (
    <div className="flex items-stretch gap-2">
      <div className="flex-shrink-0 mi-eyebrow text-white/50 text-[10px] px-2 py-2 border mi-line bg-black/50 min-w-[100px]">
        {label}
      </div>
      <div className="flex-1 min-w-0 font-mono text-[11px] text-white/80 truncate px-3 py-2 border mi-line bg-black/50">
        {url}
      </div>
      <button
        onClick={onCopy}
        data-testid={testId}
        className={`flex-shrink-0 px-3 py-2 border mi-line-strong text-[10px] mi-eyebrow inline-flex items-center gap-1 transition-colors ${
          copied ? 'bg-[#E63946] text-white border-[#E63946]' : 'bg-black/50 text-white/80 hover:bg-[#E63946] hover:text-white'
        }`}
      >
        {copied ? <><CheckCircle2 className="w-3 h-3" /> COPIED</> : <><Copy className="w-3 h-3" /> COPY</>}
      </button>
    </div>
  );
}

